import React, {useCallback, useState} from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import {CM_COLLECTION_METHOD_OPTIONS} from '../collectionTaskPayload';
import {looksLikeHtml} from '../../../utils/sourceImportHelpers';
import InheritedClassificationDisplay from './InheritedClassificationDisplay';

/**
 * Dedicated "Add Material" dialog for the Copy & Paste (raw text) entry point.
 * Calls onImport with {sourceKind:'raw', title, htmlContent|plainText, collectionMethod, classificationLevel, distribution, compartment}.
 * Classification is inherited from the parent Collection Task and is not editable here.
 */
export default function CollectionMaterialPasteImportDialog({
    open,
    onClose,
    onImport,
    busy = false,
    initialClassificationLevel = 'U',
    initialDistribution = [],
    initialCompartment = [],
}) {
    const [pasteText, setPasteText] = useState('');
    const [pasteName, setPasteName] = useState('');
    const [collectionMethod, setCollectionMethod] = useState('OSINT');
    const [error, setError] = useState(null);

    const inheritedLevel = initialClassificationLevel || 'U';
    const inheritedDistribution = Array.isArray(initialDistribution) ? initialDistribution : [];
    const inheritedCompartment = Array.isArray(initialCompartment) ? initialCompartment : [];

    const resetState = useCallback(() => {
        setPasteText('');
        setPasteName('');
        setCollectionMethod('OSINT');
        setError(null);
    }, []);

    const handleClose = useCallback(() => {
        if (busy) return;
        resetState();
        onClose?.();
    }, [busy, onClose, resetState]);

    const handleSubmit = useCallback(async () => {
        setError(null);
        const text = String(pasteText || '').trim();
        if (!text) {
            setError('Paste some content to import.');
            return;
        }
        try {
            const isHtml = looksLikeHtml(text);
            await onImport?.({
                sourceKind: 'raw',
                title: (pasteName || '').trim() || undefined,
                htmlContent: isHtml ? text : undefined,
                plainText: isHtml ? undefined : text,
                collectionMethod,
                classificationLevel: inheritedLevel,
                distribution: inheritedDistribution.length ? inheritedDistribution : undefined,
                compartment: inheritedCompartment.length ? inheritedCompartment : undefined,
            });
            resetState();
            onClose?.();
        } catch (e) {
            setError(e?.message || 'Import failed');
        }
    }, [
        collectionMethod,
        inheritedCompartment,
        inheritedDistribution,
        inheritedLevel,
        onClose,
        onImport,
        pasteName,
        pasteText,
        resetState,
    ]);

    const submitDisabled = busy || !pasteText.trim();

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <NotesOutlinedIcon fontSize="small" color="action"/>
                    <span>Add Collected Material: Raw text</span>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <TextField
                        label="Title (optional)"
                        value={pasteName}
                        onChange={(e) => setPasteName(e.target.value)}
                        fullWidth
                        size="small"
                    />

                    <FormControl size="small" fullWidth>
                        <InputLabel id="cm-paste-import-method-label">Collection Method</InputLabel>
                        <Select
                            labelId="cm-paste-import-method-label"
                            label="Collection Method"
                            value={collectionMethod}
                            onChange={(e) => setCollectionMethod(e.target.value)}
                        >
                            {CM_COLLECTION_METHOD_OPTIONS.map((method) => (
                                <MenuItem key={method} value={method}>{method}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <InheritedClassificationDisplay
                        level={inheritedLevel}
                        distribution={inheritedDistribution}
                        compartment={inheritedCompartment}
                    />

                    <TextField
                        label="Content"
                        placeholder="Paste text or HTML here…"
                        value={pasteText}
                        onChange={(e) => setPasteText(e.target.value)}
                        fullWidth
                        multiline
                        minRows={6}
                        helperText="Paste plain text or HTML as raw collected content."
                    />
                </Stack>

                {error && (
                    <Alert severity="error" sx={{mt: 2}}>{error}</Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={busy}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={submitDisabled}>
                    {busy ? 'Importing…' : 'Add material'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
